import { getPonticsData } from 'Domain/Scene/Handler/AnimationHandler'
import { Store } from 'Application/Store/Store'
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine'
import { animationStore } from 'Domain/Mesh/State/AnimationStore'

/**
 * Fournit la structure du modèle dentaire pour le ModelSelector
 * Cette fonction peut être modifiée ou remplacée pour changer la structure affichée
 * 
 * @param {Scene} scene - La scène Three.js
 * @returns {object} Structure hiérarchique du modèle dentaire
 * 
 * Format de retour attendu:
 * {
 *   [key]: {
 *     name: string,           // Nom affiché
 *     items?: Mesh[],         // Meshes à ce niveau (optionnel)
 *     children?: {            // Sous-groupes (optionnel)
 *       [childKey]: {
 *         name: string,
 *         items: Mesh[],
 *         expandable?: boolean  // Si on peut voir les items individuellement
 *       }
 *     }
 *   }
 * }
 */
export const getDentalModelStructure = (scene) => {
    const viewerType = Store.getState().ViewportReducer.viewerType;
    if (viewerType === 'simple') {
        return getDentalModelStructureSimple(scene);
    } else {
        return getDentalModelStructureDefault(scene);
    }
}

/**
 * Convertit un mesh en objet de données sérialisable (sans objet Three.js)
 * @param {Mesh} mesh - Mesh Three.js
 * @returns {Object} - Données du mesh (uuid, name, visible, etc.)
 */
const meshToData = (mesh) => ({
    uuid: mesh.uuid,
    name: mesh.name,
    visible: mesh.visible,
    userData: {
        jawType: mesh.userData?.jawType,
        type: mesh.userData?.type,
        toothID: mesh.userData?.toothID
    }
});

/**
 * Version par défaut de la structure du modèle dentaire
 * @param {Scene} scene - La scène Three.js
 * @returns {object} Structure hiérarchique complète avec des données sérialisables (pas d'objets Three.js)
 */
export const getDentalModelStructureDefault = (scene) => {
    const importedMeshes = scene.getObjectByName('importedMeshes')
    const mandiGroup = scene.getObjectByName('mandiGroup')
    
    if (!importedMeshes) return null
    
    // Collecter les dents maxillaires
    const maxiTeeth = []
    importedMeshes.traverse(mesh => {
        if (!mesh.isMesh || !mesh.userData) return
        if (mesh.userData.jawType === 'U' && mesh.userData.type === 0) {
            maxiTeeth.push(meshToData(mesh))
        }
    })
    
    // Collecter les dents mandibulaires depuis mandiGroup (ByteJump)
    const mandiTeeth = []
    if (mandiGroup) {
        mandiGroup.traverse(mesh => {
            if (!mesh.isMesh || !mesh.userData) return
            if (mesh.userData.jawType === 'L' && mesh.userData.type === 0 && mesh.name.startsWith('0_Tooth_')) {
                mandiTeeth.push(meshToData(mesh))
            }
        })
    }
    
    // Fallback: utiliser allMandiTeeth si mandiGroup n'a pas les dents
    const allMandiTeeth = threeEngine.getAllMandiTeeth();
    if (mandiTeeth.length === 0 && allMandiTeeth.length > 0) {
        allMandiTeeth.forEach(mesh => {
            if (mesh.userData?.type === 0 && mesh.name.startsWith('0_Tooth_')) {
                mandiTeeth.push(meshToData(mesh))
            }
        })
    }
    
    // Collecter les gencives
    const gengivas = animationStore.getGengivas();
    const maxiGengivas = Object.values(gengivas.maxi || {}).map(meshToData)
    const mandiGengivas = Object.values(gengivas.mandi || {}).map(meshToData)
    
    // Collecter les pontics (les clés sont les numéros de dent, pas maxi/mandi)
    const ponticsData = getPonticsData();
    const maxiPontics = []
    const mandiPontics = []
    Object.entries(ponticsData).forEach(([position, ponticsByStep]) => {
        Object.values(ponticsByStep).forEach(mesh => {
            if (mesh.userData?.jawType === 'U') {
                maxiPontics.push(meshToData(mesh))
            } else if (mesh.userData?.jawType === 'L') {
                mandiPontics.push(meshToData(mesh))
            }
        })
    })
    
    // Construire la structure pour chaque arcade
    const buildArchStructure = (name, teeth, gencivasList, ponticsList) => {
        const children = {
            teeth: {
                name: 'Dents',
                items: teeth,
                expandable: true
            },
            gengivas: {
                name: 'Gencive',
                items: gencivasList,
                expandable: false
            }
        }
        
        // Ajouter les pontics seulement s'il y en a
        if (ponticsList.length > 0) {
            children.pontics = {
                name: 'Pontics',
                items: ponticsList,
                expandable: false
            }
        }
        
        return { name, children }
    }
    
    return {
        maxi: buildArchStructure('Maxillaire', maxiTeeth, maxiGengivas, maxiPontics),
        mandi: buildArchStructure('Mandibulaire', mandiTeeth, mandiGengivas, mandiPontics)
    }
}

/**
 * Version simple de la structure du modèle dentaire (pour viewer simplifié)
 * @param {Scene} scene - La scène Three.js
 * @returns {object} Structure simplifiée avec des données sérialisables
 */
export const getDentalModelStructureSimple = (scene) => {
    const importedMeshes = scene.getObjectByName('importedMeshes')
    
    if (!importedMeshes) return null
    
    // Collecter tous les meshes visibles dans une liste plate
    const allMeshes = []
    importedMeshes.traverse(mesh => {
        if (mesh.isMesh) {
            allMeshes.push(meshToData(mesh))
        }
    })
    
    // Retourner une structure simple avec tous les meshes dans un seul groupe
    return {
        model: {
            name: 'Modèle 3D',
            items: allMeshes,
            expandable: false
        }
    }
}
